import React from 'react';
import {HashRouter as Router, Route} from "react-router-dom";
import PropTypes from 'prop-types';

// Components
import {History} from '../components/History/History';
import {Header} from '../components/Header/Header';
import {Display} from '../components/Display/Display';
import {Keypad} from '../components/Keypad/Keypad';

// Style sheets
import Style from './App.module.scss';
import '../Default.scss';

export default class App extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      currentPressed: "",
      currentPressedList: [],
      firstNumber: null,
      operator: null,
      secondNumber: null,
      total: null,
      calculation: null,
      complete: false,
      history: [],
      darkMode: false,
    }
  }

  static propTypes =  {
    currentPressed: PropTypes.string,
    currentPressedList: PropTypes.array,
    firstNumber: PropTypes.number,
    operator: PropTypes.string,
    secondNumber: PropTypes.number,
    total: PropTypes.number,
    calculation: PropTypes.string,
    complete: PropTypes.bool,
    history: PropTypes.array,
    darkMode: PropTypes.bool,
  }

  componentDidMount(){ 
    // Will set a variables with the value of the local storage 
    const setHistory = JSON.parse(localStorage.getItem("History") || "[]");
    const setDarkMode = JSON.parse(localStorage.getItem("DarkMode") || false);

    this.setState(state => ({
      history: setHistory,
      darkMode: setDarkMode
    }))

    document.addEventListener("keydown", this.handleKeypress, false);
  }

  componentWillUnmount(){
    document.removeEventListener("keydown", this.handleKeypress, false);
  }

  // This method will check which key the user pressed and will open the necessary method to execute
  handleKeypress = (e) => {
    const keyList = ["0", ".", "1", "2", "3", "4", "5", "6", "7", "8", "9", "/", "*", "-", "+", "%"];

    (keyList.includes(e.key)) ? this.handleClick(e.key)
      : (e.key === "Escape") ? this.handleClear() 
        : (e.key === "Enter") ? this.handleCalculation()
          : (e.key === "Backspace" || e.key === "Delete") ? this.handleBackspace() : this.handleReturn()
  }

  // This Method will return nothing
  handleReturn(){
    return
  }

  // This method will handle anytime the user inputs something
  handleClick = (title) => {
    // Will get the current state of currentPressed & currentPressedList
    let currentPressed = this.state.currentPressed;
    const newCurrentPressedList = [...this.state.currentPressedList];

    // Adding the input from the user into newCurrentPressedList array
    newCurrentPressedList.push(title) 

    // Checking if complete is true if yes skip
    if(this.state.complete === true){
      if(title === "/" || title === "*" || title === "-" || title === "+" || title === "%"){
        return
      } else {
        this.setState({
          currentPressed: currentPressed += title,
          currentPressedList: newCurrentPressedList,
          calculation: `${this.state.firstNumber} ${this.state.operator} ${this.state.secondNumber} = ${this.state.total}`,
          total: null,
          complete: false,
        });
        return
      }
    } else if(title === "/" || title === "*" || title === "-" || title === "+" || title === "%"){ // If user presses any of the operators
        this.setState({
          currentPressed: "",
          currentPressedList: [],
          firstNumber: parseFloat(currentPressed),
          operator: title,
          calculation: null,
        });
      } else if(currentPressed.length > 11){ // If currentPressed string length is bigger or equal to 12 will stop the user from inputting
        this.setState({
          currentPressed: "Exceeded limit!",
          calculation: "Limit 12 Numbers (Delete to continue or Clear)",
        })
        return
      } else {
        this.setState(state => ({
          currentPressed: state.currentPressed += title,
          currentPressedList: newCurrentPressedList,
          secondNumber: parseFloat(state.currentPressed),
        }));
      }  
  }

  // This method will reset the state except the history array
  handleClear = () => {
    this.setState({
      currentPressed: "",
      currentPressedList: [],
      firstNumber: null,
      operator: null,
      secondNumber: null,
      total: null,
      calculation: null,
      complete: false,
    });
  }

  // This method will reset the history array & will delete the local storage
  handleClearHistory = () => {
    this.setState(state => ({
      history: [],
    }))

    localStorage.removeItem("History");
  }

  // This method will calculate the numbers that the user chose 
  handleCalculation = () => {
    // Get current state and save them in variables
    const firstNumber = this.state.firstNumber;
    const operator = this.state.operator;
    const secondNumber = this.state.secondNumber;
    let total = null;

    // Get current state of the history array and clone it into newHistory array
    const newHistory = [...this.state.history];

    // Checking if either firstNumber/operator/secondNumber are null if so skip
    if(firstNumber === null || operator === null || secondNumber === null){
      return
    }else{

      // Calculate
      switch(operator){
        case "%":
          total = firstNumber % secondNumber;
          break;
        case "/":
          total = firstNumber / secondNumber;
          break;
        case "*":
          total = firstNumber * secondNumber;
          break;
        case "-":
          total = firstNumber - secondNumber;
          break;
        case "+":
          total = firstNumber + secondNumber;
          break;
        default:
          break;
      }

      // Creating a new item to put into the History array
      const newHistoryItem = {
        id: Math.random().toString(36).substr(2, 9),
        firstNumber: firstNumber,
        operator: operator,
        secondNumber: secondNumber,
        total: (parseFloat(total) >= 1000000000) ? total = parseFloat(total).toExponential(3) : total,
      }

      // Adding the newHistoryItem into History array
      newHistory.push(newHistoryItem);

      // Setting the History array into our local storage
      localStorage.setItem("History", JSON.stringify(newHistory));

      this.setState({
        currentPressed: "",
        currentPressedList: [],
        calculation: `${firstNumber} ${operator} ${secondNumber}`,
        total: (parseFloat(total) >= 1000000000) ? total = parseFloat(total).toExponential(3) : total,
        complete: true,
        history: newHistory,
      })
    }
  }

  // This method will remove the last digit that the user inputted
  handleBackspace = () => {
    // Checking if complete is true if yes skip
    if(this.state.complete === true){
      return;
    }else{
      // Get current state of the currentPressedList array and clone it into newCurrentPressedList array
      const newCurrentPressedList = [...this.state.currentPressedList];

      // Removing the last item in the array
      newCurrentPressedList.pop();
      
      // Putting back the array into a single String
      const newCurrentPressed = newCurrentPressedList.join("");

      this.setState(state => ({
        currentPressed: String(newCurrentPressed),
        currentPressedList: newCurrentPressedList,
        secondNumber: parseFloat(newCurrentPressed),
        calculation: (String(state.calculation).length > 26 ? null : state.calculation),
      }));
    }
  }

  // This method will toggle the dark/light mode
  handleDarkMode = () => {
    localStorage.setItem("DarkMode", !this.state.darkMode)
    this.setState(state => ({
      darkMode: !state.darkMode
    }))
  }

  render(){
    const containerStyle = this.state.darkMode === true ? `${Style.container} ${Style.Black} animated fade` : `${Style.container} ${Style.White} animated fade`;

    return (

      <Router>
        <div className={`${Style.Horizontal} animated fade`}>
          <div className={`${Style.Message} animated flash`}>
            <h1 className={Style.Header}>Please rotate your device</h1>
            <h4 className={Style.Subheader}>We don't support landscape mode yet on mobile. Please go back to portrait mode for the best experience. Thank you.</h4>
          </div>
        </div>
        {/* Calculator Page */}
        <Route exact path="/">
          <div className={containerStyle}>
            <Header currentPressed={this.state.currentPressed} currentPage="/" onClickDarkLightMode={this.handleDarkMode} darkMode={this.state.darkMode} />
            <Display total={this.state.total} currentPressed={this.state.currentPressed} calculation={this.state.calculation} darkMode={this.state.darkMode} />
            <Keypad onClick={this.handleClick} onClickHandleClear={this.handleClear} onClickHandleBackspace={this.handleBackspace} onClickHandleCalculation={this.handleCalculation} onKeypress={this.handleKeypress} darkMode={this.state.darkMode} />
          </div>
        </Route>
        {/* History Page */}
        <Route path="/history">
          <div className={containerStyle}>
            <Header currentPage="/history" onClickDarkLightMode={this.handleDarkMode} onClickHandleClear={this.handleClear} darkMode={this.state.darkMode} />
            <History history={this.state.history} onClickClearHistory={this.handleClearHistory} />
          </div>
        </Route>
      </Router>
    );
  };
}